import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
  { n: "01", k: "Delve", t: "Audit + Insight", d: "We dig into data, customers, and competitive context to surface what's true." },
  { n: "02", k: "Design", t: "Strategy + Creative", d: "We translate insight into a sharp plan and a brand experience worth feeling." },
  { n: "03", k: "Build", t: "Development + Automation", d: "We ship — fast, beautiful systems wired with AI and automation from day one." },
  { n: "04", k: "Venture", t: "Launch + Scale", d: "We compound results, optimizing performance and unlocking new markets." },
];

const Process = () => {
  const sectionRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!sectionRef.current || !lineRef.current) return;

    if (reduce) {
      lineRef.current.style.transform = "scaleY(1)";
      return;
    }

    const ctx = gsap.context(() => {
      gsap.fromTo(
        lineRef.current,
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            end: "bottom 80%",
            scrub: 0.6,
          },
        }
      );

      gsap.utils.toArray(".process-step").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "expo.out",
            scrollTrigger: { trigger: el, start: "top 85%" },
          }
        );
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="process-section"
      id="process"
      className="relative px-6 md:px-12 lg:px-20 py-32 md:py-40"
    >
      <div className="flex items-end justify-between mb-16 md:mb-20 flex-wrap gap-6">
        <div className="flex items-center gap-4">
          <span className="h-px w-12 bg-[#0a0a0b]/30" />
          <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">04 — Process</span>
        </div>
        <h2
          className="font-display font-medium tracking-tight leading-none text-[#0a0a0b]"
          style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)" }}
        >
          From delve to <span className="accent-text">venture.</span>
        </h2>
      </div>

      <div className="relative max-w-5xl mx-auto pl-10 md:pl-16">
        {/* connecting line */}
        <div className="absolute left-3 md:left-5 top-2 bottom-2 w-px bg-[#ededed]/10">
          <div
            ref={lineRef}
            className="absolute inset-x-0 top-0 origin-top accent-gradient"
            style={{ height: "100%", transform: "scaleY(0)" }}
          />
        </div>

        <div className="space-y-16 md:space-y-24">
          {STEPS.map((s, i) => (
            <div key={s.n} className="process-step relative" data-testid={`process-step-${i}`}>
              <div
                className="absolute -left-[34px] md:-left-[50px] top-2 w-3 h-3 rounded-full accent-gradient ring-4 ring-[#0a0a0b]"
                aria-hidden
              />
              <div className="flex items-baseline gap-4 md:gap-6 flex-wrap">
                <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">{s.n}</span>
                <h3
                  className="font-display font-medium tracking-tight text-[#0a0a0b] leading-none"
                  style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}
                >
                  {s.k}
                </h3>
                <span className="text-sm uppercase tracking-[0.3em] text-muted-ink">{s.t}</span>
              </div>
              <p className="mt-4 max-w-2xl text-base md:text-lg text-muted-ink">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
